import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DatePicker, Select, Input } from 'antd';
import { PeopleAlt, EmojiEvents, GetApp, Phone, DateRange as DateRangeIcon, ArrowDropDown, Language } from '@material-ui/icons';
import toast from 'components/Basic/Toast';
import { useLazyGetActiveCampaignsQuery } from 'core/worldCup/customerSupport/CustomerSupportService';
import { useLazyGetPrizeDrawsListQuery } from 'core/worldCup/prizeDraws/PrizeDrawsService';
import { useLazyDownloadRaffleRoundsReportQuery, useLazyDownloadParticipantsReportQuery } from 'core/worldCup/reports/WorldCupReportsService';
import { Campaign } from 'core/worldCup/customerSupport/types';
import { PrizeDraw } from 'core/worldCup/prizeDraws/types';

const { Option } = Select;

const Reports: React.FC = () => {
  const intl = useIntl();

  // ── Campaigns ──────────────────────────────────────────────────────────
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);
  const [getActiveCampaigns] = useLazyGetActiveCampaignsQuery();

  // ── Prize Draws ────────────────────────────────────────────────────────
  const [prizeDraws, setPrizeDraws] = useState<PrizeDraw[]>([]);
  const [selectedDrawId, setSelectedDrawId] = useState<number | null>(null);
  const [getPrizeDrawsList] = useLazyGetPrizeDrawsListQuery();

  // ── Participants report filters ────────────────────────────────────────
  const [participantsFilters, setParticipantsFilters] = useState({
    phone: '',
    dateFrom: '',
    dateTo: '',
    verbose: 'es' as 'en' | 'es',
  });

  // ── Raffle rounds report filters ───────────────────────────────────────
  const [raffleVerbose, setRaffleVerbose] = useState<'en' | 'es'>('es');

  // ── Download state ─────────────────────────────────────────────────────
  const [downloadingParticipants, setDownloadingParticipants] = useState(false);
  const [downloadingRaffle, setDownloadingRaffle] = useState(false);

  const [downloadRaffleRoundsReport] = useLazyDownloadRaffleRoundsReportQuery();
  const [downloadParticipantsReport] = useLazyDownloadParticipantsReportQuery();

  // ── Init ───────────────────────────────────────────────────────────────
  useEffect(() => {
    loadCampaigns();
  }, []);

  useEffect(() => {
    if (selectedCampaignId) {
      loadPrizeDraws(selectedCampaignId);
    } else {
      setPrizeDraws([]);
      setSelectedDrawId(null);
    }
  }, [selectedCampaignId]);

  const loadCampaigns = async () => {
    try {
      const res: any = await getActiveCampaigns().unwrap();
      const list: Campaign[] = Array.isArray(res) ? res : res?.data ?? [];
      setCampaigns(list);
      if (list.length === 1) setSelectedCampaignId(list[0].id);
    } catch (e) {
      console.error(e);
    }
  };

  const loadPrizeDraws = async (campaignId: number) => {
    try {
      const qs = `campaignId=${campaignId}&page=1&limit=100&sort=id&sortDirection=desc`;
      const res = await getPrizeDrawsList({ queryString: qs }).unwrap();
      const completed = (res.data || []).filter((d: PrizeDraw) => d.status === 'COMPLETED');
      setPrizeDraws(completed);
    } catch (e) {
      console.error(e);
    }
  };

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleDownloadParticipants = async () => {
    if (!selectedCampaignId) {
      toast.error({ title: intl.formatMessage({ id: 'Please select a campaign' }) });
      return;
    }
    setDownloadingParticipants(true);
    try {
      const params: any = {
        campaignId: selectedCampaignId,
        verbose: participantsFilters.verbose,
      };
      if (participantsFilters.phone) params.phone = participantsFilters.phone;
      if (participantsFilters.dateFrom) params.dateFrom = participantsFilters.dateFrom;
      if (participantsFilters.dateTo) params.dateTo = participantsFilters.dateTo;

      const blob = await downloadParticipantsReport(params).unwrap();
      triggerDownload(blob, `participants-campaign-${selectedCampaignId}-${Date.now()}.csv`);
      toast.success({ title: intl.formatMessage({ id: 'Participants report downloaded successfully' }) });
    } catch (e: any) {
      const code = e?.data?.code;
      if (code === 'ReportsCampaignNotFound') {
        toast.error({ title: intl.formatMessage({ id: 'Campaign not found for report' }) });
      } else {
        toast.error({ title: intl.formatMessage({ id: 'Error downloading participants report' }) });
      }
    } finally {
      setDownloadingParticipants(false);
    }
  };

  const handleDownloadRaffleRounds = async () => {
    if (!selectedDrawId) {
      toast.error({ title: intl.formatMessage({ id: 'Please select a prize draw' }) });
      return;
    }
    setDownloadingRaffle(true);
    try {
      const blob = await downloadRaffleRoundsReport({ raffleConfigId: selectedDrawId, verbose: raffleVerbose }).unwrap();
      triggerDownload(blob, `raffle-rounds-${selectedDrawId}-${Date.now()}.csv`);
      toast.success({ title: intl.formatMessage({ id: 'Winners report downloaded successfully' }) });
    } catch (e: any) {
      const code = e?.data?.code;
      if (code === 'ReportsRaffleConfigNotFound') {
        toast.error({ title: intl.formatMessage({ id: 'Raffle config not found' }) });
      } else {
        toast.error({ title: intl.formatMessage({ id: 'Error downloading winners report' }) });
      }
    } finally {
      setDownloadingRaffle(false);
    }
  };

  const triggerDownload = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const verboseOptions = [
    { value: 'es', label: 'Español' },
    { value: 'en', label: 'English' },
  ];

  return (
    <div className="wc_reports_page">

      {/* ── Page header ─────────────────────────────────────────── */}
      <div className="wc_reports_header">
        <div className="wc_reports_header_left">
          <h1 className="wc_reports_title">
            <FormattedMessage id="Reports" />
          </h1>
          <p className="wc_reports_subtitle">
            <FormattedMessage id="Reports and analytics for the World Cup module" />
          </p>
        </div>

        {campaigns.length > 1 && (
          <div className="wc_reports_campaign_pill">
            <span className="wc_reports_campaign_label">
              <FormattedMessage id="Campaign" />
            </span>
            <Select
              className="wc_reports_campaign_select"
              suffixIcon={<ArrowDropDown />}
              placeholder={intl.formatMessage({ id: 'Select campaign' })}
              dropdownStyle={{ paddingLeft: 0, paddingRight: 0 }}
              value={selectedCampaignId || undefined}
              onChange={(v: number) => setSelectedCampaignId(v)}
              style={{ minWidth: 220 }}
            >
              {campaigns.map(c => (
                <Option key={c.id} value={c.id}>{c.name}</Option>
              ))}
            </Select>
          </div>
        )}
      </div>

      {/* ── Cards grid ──────────────────────────────────────────── */}
      <div className="wc_reports_grid">

        {/* ── Card 1: Participants ─────────────────────────────── */}
        <div className="wc_report_card">
          <div className="wc_report_card_header">
            <div className="wc_report_card_icon">
              <PeopleAlt />
            </div>
            <div>
              <h2 className="wc_report_card_title">
                <FormattedMessage id="Participants report" />
              </h2>
              <p className="wc_report_card_desc">
                <FormattedMessage id="Download all participants of the selected campaign as CSV" />
              </p>
            </div>
          </div>

          <div className="wc_report_card_body">

            <div className="wc_report_field">
              <label className="wc_report_field_label">
                <Language fontSize="inherit" />
                <FormattedMessage id="CSV language" />
              </label>
              <Select
                className="wc_report_select"
                suffixIcon={<ArrowDropDown />}
                value={participantsFilters.verbose}
                onChange={v => setParticipantsFilters(prev => ({ ...prev, verbose: v }))}
                dropdownStyle={{ paddingLeft: 0, paddingRight: 0 }}
              >
                {verboseOptions.map(o => (
                  <Option key={o.value} value={o.value}>{o.label}</Option>
                ))}
              </Select>
            </div>

            <div className="wc_report_field">
              <label className="wc_report_field_label">
                <Phone fontSize="inherit" />
                <FormattedMessage id="Filter by phone" />
                <span className="wc_report_field_optional">
                  <FormattedMessage id="optional" />
                </span>
              </label>
              <Input
                className="wc_report_input"
                placeholder="+502..."
                value={participantsFilters.phone}
                onChange={e => setParticipantsFilters(prev => ({ ...prev, phone: e.target.value }))}
                allowClear
              />
            </div>

            <div className="wc_report_field_row">
              <div className="wc_report_field">
                <label className="wc_report_field_label">
                  <DateRangeIcon fontSize="inherit" />
                  <FormattedMessage id="From" />
                  <span className="wc_report_field_optional">
                    <FormattedMessage id="optional" />
                  </span>
                </label>
                <DatePicker
                  className="wc_report_datepicker"
                  placeholder="yyyy-MM-dd"
                  format="YYYY-MM-DD"
                  suffixIcon={null}
                  onChange={(_d, s) => setParticipantsFilters(prev => ({ ...prev, dateFrom: s as string }))}
                />
              </div>
              <div className="wc_report_field">
                <label className="wc_report_field_label">
                  <DateRangeIcon fontSize="inherit" />
                  <FormattedMessage id="To" />
                  <span className="wc_report_field_optional">
                    <FormattedMessage id="optional" />
                  </span>
                </label>
                <DatePicker
                  className="wc_report_datepicker"
                  placeholder="yyyy-MM-dd"
                  format="YYYY-MM-DD"
                  suffixIcon={null}
                  onChange={(_d, s) => setParticipantsFilters(prev => ({ ...prev, dateTo: s as string }))}
                />
              </div>
            </div>
          </div>

          <div className="wc_report_card_footer">
            <button
              className={`wc_report_download_btn ${downloadingParticipants ? 'loading' : ''} ${!selectedCampaignId ? 'disabled' : ''}`}
              onClick={handleDownloadParticipants}
              disabled={downloadingParticipants || !selectedCampaignId}
            >
              {downloadingParticipants ? (
                <span className="wc_report_btn_spinner" />
              ) : (
                <GetApp />
              )}
              <span>
                {downloadingParticipants
                  ? intl.formatMessage({ id: 'Downloading...' })
                  : intl.formatMessage({ id: 'Download CSV' })}
              </span>
            </button>
            {!selectedCampaignId && (
              <p className="wc_report_hint">
                <FormattedMessage id="Select a campaign to enable download" />
              </p>
            )}
          </div>
        </div>

        {/* ── Card 2: Raffle Rounds (Winners) ─────────────────── */}
        <div className="wc_report_card">
          <div className="wc_report_card_header">
            <div className="wc_report_card_icon accent_gold">
              <EmojiEvents />
            </div>
            <div>
              <h2 className="wc_report_card_title">
                <FormattedMessage id="Winners report" />
              </h2>
              <p className="wc_report_card_desc">
                <FormattedMessage id="Download winners of a completed prize draw as CSV" />
              </p>
            </div>
          </div>

          <div className="wc_report_card_body">

            <div className="wc_report_field">
              <label className="wc_report_field_label">
                <EmojiEvents fontSize="inherit" />
                <FormattedMessage id="Prize draw" />
              </label>
              <Select
                className="wc_report_select"
                suffixIcon={<ArrowDropDown />}
                placeholder={
                  !selectedCampaignId
                    ? intl.formatMessage({ id: 'Select a campaign first' })
                    : prizeDraws.length === 0
                    ? intl.formatMessage({ id: 'No completed draws' })
                    : intl.formatMessage({ id: 'Select prize draw' })
                }
                dropdownStyle={{ paddingLeft: 0, paddingRight: 0 }}
                value={selectedDrawId || undefined}
                onChange={(v: number) => setSelectedDrawId(v)}
                disabled={!selectedCampaignId || prizeDraws.length === 0}
              >
                {prizeDraws.map(d => (
                  <Option key={d.id} value={d.id}>
                    <span className="wc_draw_option">
                      <span>{d.name}</span>
                      <span className="wc_draw_option_id">#{d.id}</span>
                    </span>
                  </Option>
                ))}
              </Select>
              {selectedCampaignId && prizeDraws.length === 0 && (
                <span className="wc_report_no_draws">
                  <FormattedMessage id="No completed draws available for this campaign" />
                </span>
              )}
            </div>

            <div className="wc_report_field">
              <label className="wc_report_field_label">
                <Language fontSize="inherit" />
                <FormattedMessage id="CSV language" />
              </label>
              <Select
                className="wc_report_select"
                suffixIcon={<ArrowDropDown />}
                value={raffleVerbose}
                onChange={(v: 'en' | 'es') => setRaffleVerbose(v)}
                dropdownStyle={{ paddingLeft: 0, paddingRight: 0 }}
              >
                {verboseOptions.map(o => (
                  <Option key={o.value} value={o.value}>{o.label}</Option>
                ))}
              </Select>
            </div>

            <div className="wc_report_winners_info">
              <span className="wc_report_winners_info_dot" />
              <span>
                <FormattedMessage id="Only WINNER entries are included in this report" />
              </span>
            </div>
          </div>

          <div className="wc_report_card_footer">
            <button
              className={`wc_report_download_btn accent_gold ${downloadingRaffle ? 'loading' : ''} ${!selectedDrawId ? 'disabled' : ''}`}
              onClick={handleDownloadRaffleRounds}
              disabled={downloadingRaffle || !selectedDrawId}
            >
              {downloadingRaffle ? (
                <span className="wc_report_btn_spinner" />
              ) : (
                <GetApp />
              )}
              <span>
                {downloadingRaffle
                  ? intl.formatMessage({ id: 'Downloading...' })
                  : intl.formatMessage({ id: 'Download CSV' })}
              </span>
            </button>
            {!selectedDrawId && selectedCampaignId && prizeDraws.length > 0 && (
              <p className="wc_report_hint">
                <FormattedMessage id="Select a prize draw to enable download" />
              </p>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Reports;
